import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'colabella_auth';

// Set up axios interceptor to add auth header to ALL requests
axios.interceptors.request.use((config) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const { session_token } = JSON.parse(stored);
      if (session_token) {
        config.headers.Authorization = `Bearer ${session_token}`;
      }
    }
  } catch {}
  return config;
});

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Check if we have a stored token
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const { session_token } = JSON.parse(stored);
      if (!session_token) throw new Error('No token');
      
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${session_token}` }
      });
      setUser(res.data);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {}
    localStorage.removeItem(STORAGE_KEY);
    setUser(null);
  };

  const saveAuth = (data) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setUser(data);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser: saveAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
